const wrap = require('word-wrap')
const splitter = require('sbd')
const log = require('@showrunner/log')('script-converter')
const { parser } = require('./utils')

const LINES = 56 // number of lines on a page

const widths = {
  action: 60,
  dialogue: 34,
  dual_dialogue: 20,
  character: 30,
  parenthetical: 32,
  dual_parenthetical: 20,
  dual_character: 15,
  transition: 48
}

function insertPageBreaks (tokens) {
  const breaks = []
  let lineCount = 0
  let pages = 2 // page 1 covered by title page
  let dialogueBlock = false // do we have to calculate a page break within a block of dialogue?
  let speaking = {} // keep track of who is speaking in case we have to break dialogue
  let remainder = 0 // lines left on the page when we encounter a token that goes over the line limit
  let endedOnParen = false

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]
    const numLines = token.lines || 0
    let leftover = 0
    // if we're in a dialogue block, we already added the lines since
    // dialogue_begin contains the total line count
    // we also don't want to add notes to total lines since they aren't printed
    const isNote = token.type === 'note' || token.type === 'note_inline'
    if (!dialogueBlock && !isNote) lineCount += numLines
    if (token && token.type === 'dialogue_begin') {
      dialogueBlock = true
    }
    if (token.type === 'dialogue_end') dialogueBlock = false
    log.trace('Token: ', token)
    log.trace('Lines after: ', lineCount)
    if (lineCount > LINES) { // PAGE BREAK ALERT!!!!!
      log.trace('+++++++ PAGE BREAK +++++++++')
      const diff = lineCount - LINES
      // rules for breaking by structure type
      if (token.type === 'dialogue_begin') {
        remainder = token.lines - diff
        if (remainder < 6) {
          // if we just started a scene and don't have that much room left on a page,
          // grab the scene heading and push it along with the dialogue after it to a new page
          const currToken = tokens[i - 1]
          if (currToken && currToken.type === 'scene_heading') {
            const scene = breaks.pop()
            breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
            breaks.push(scene)
            breaks.push(token)
            dialogueBlock = false
            remainder = 0
          } else {
            // not that much room left, start this whole dialogue block on a new page
            breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
            breaks.push(token)
            dialogueBlock = false
            remainder = 0
          }
          // account for the one dialogue block starting line, on the new page
          // we'll add the character and speech lines in the next loops
          leftover = 1
        } else {
          // there's a lot of room left on the page, so go through each token and see
          // where to break the page (rules below in character, dialogue, and parentheses rules)
          breaks.push(token)
          dialogueBlock = true
        }
      } else if (token.type === 'character') {
        remainder -= token.lines
        speaking = token
        // character can't go on its own line, so if there's only room
        // for the character token left on a page let's start a new page
        if (remainder < token.lines) {
          breaks.push({ type: 'dialogue_end' })
          breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
          breaks.push({ type: 'dialogue_begin' })
          breaks.push(token)
          dialogueBlock = false
          remainder = 0
        } else {
          breaks.push(token)
        }
      } else if (token.type === 'dialogue') {
        // do we have room left for this dialogue?
        if (remainder > token.lines) { // we do!
          breaks.push(token)
          remainder -= token.lines
          endedOnParen = false
        } else if (remainder < 6) { // there's not enough room left on the page to continue the dialogue block
          if (endedOnParen) breaks.push({ type: 'character', text: '(MORE)', input: '(MORE)', wrapped: '(MORE)', lines: 1, break: true, blanks: 0 }) // break flag for html converter
          breaks.push({ type: 'dialogue_end' })
          breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
          breaks.push({ type: 'dialogue_begin' })
          if (endedOnParen) {
            const post = `${speaking.text} (CONT'D)`
            const wrapped = wrap(post, { width: widths.character, indent: '' })
            breaks.push({ type: 'character', text: post, input: post, wrapped, break: true, lines: wrapped.split('\n').length, blanks: 0 })
            leftover += wrapped.split('\n').length
            endedOnParen = false
          }
          breaks.push(token)
          leftover += token.lines
          dialogueBlock = false
          remainder = 0
        } else { //  we have enough room for part of the spoken dialogue, need to break it up
          const post = `${speaking.text} (CONT'D)`
          if (endedOnParen) endedOnParen = false
          const wrapped = wrap(post, { width: widths.character, indent: '' })
          const broken = breakLines(token, diff, (remainder - (speaking.lines + 2)))
          log.trace('After line breaking: ', broken)
          // break lines can fail to break based on sentences if it can't find a good place
          if (broken[0]) breaks.push(broken[0])
          if (broken[0] && !broken[1]) {
            breaks.push({ type: 'dialogue_end' })
            breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
            breaks.push({ type: 'dialogue_begin' })
          }
          if (broken[1]) {
            let char
            if (!broken[0]) char = breaks.pop() // if the dialogue wasn't split, bring the character token down too
            if (broken[0]) breaks.push({ type: 'character', text: '(MORE)', input: '(MORE)', wrapped: '(MORE)', lines: 1, break: true, blanks: 0 }) // break flag for html converter
            breaks.push({ type: 'dialogue_end' })
            breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
            breaks.push({ type: 'dialogue_begin' })
            if (char) breaks.push(char)
            if (broken[0]) breaks.push({ type: 'character', text: post, input: post, wrapped, break: true, lines: wrapped.split('\n').length, blanks: 0 }) // break flag for html converter

            breaks.push(broken[1])
            leftover = wrapped.split('\n').length + broken[1].lines
          }
          dialogueBlock = false
          remainder = 0
        }
      } else if (token.type === 'parenthetical') {
        // with parentheticals, we don't split the contents
        // we want to fit it all on the last or next page depending on room
        // with MORE and CONT'D lines as well, since we're in dialogue
        if (remainder > token.lines) { // we can fit the parenthetical on this page
          breaks.push(token)
          remainder -= token.lines
          endedOnParen = true // we need to make sure to add (CONT'D) to the next dialogue
        } else { // push it to next page
          breaks.push({ type: 'character', text: '(MORE)', input: '(MORE)', wrapped: '(MORE)', lines: 1, break: true, blanks: 0 }) // break flag for html converter
          breaks.push({ type: 'dialogue_end' })
          breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
          breaks.push({ type: 'dialogue_begin' })
          const post = `${speaking.text} (CONT'D)`
          const wrapped = wrap(post, { width: widths.character, indent: '' })
          breaks.push({ type: 'character', text: post, input: post, wrapped, break: true, lines: wrapped.split('\n').length, blanks: 0 })
          breaks.push(token)
          endedOnParen = false // reset
          leftover += token.lines + wrapped.split('\n').length
          dialogueBlock = false
          remainder = 0
        }
      } else if (token.type === 'dialogue_end') {
        breaks.push(token) // no content
        dialogueBlock = false
        remainder = 0
      } else if (token.type === 'scene_heading') {
        // we almost always put scene headings on a new page, unless they are super long
        const remainder = token.lines - diff
        if (remainder < 3) {
          breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
          breaks.push(token)
          leftover = token.lines
        } else { // someone made a really long scene heading and there's room left on the page, so break it
          const broken = breakLines(token, diff, remainder)
          if (broken[0]) breaks.push(broken[0])
          breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
          if (broken[1]) {
            breaks.push(broken[1])
            leftover = broken[1].lines
          }
        }
      } else if (token.type === 'transition') {
        // do we have room to fit it on this page?
        const remainder = token.lines - diff
        if (remainder < 3) { // if we're at the end of a page, push it onto the next
          breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
          breaks.push(token)
          leftover = token.lines
        } else { // it's long and there's room left, break it
          const broken = breakLines(token, diff, remainder)
          if (broken[0]) breaks.push(broken[0])
          breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
          if (broken[1]) {
            breaks.push(broken[1])
            leftover = broken[1].lines
          }
        }
      } else { // structure types not covered above
        if (token.wrapped) { // break the wrapped lines
          const remainder = token.lines - diff
          const broken = breakLines(token, diff, remainder)
          let scene
          const currToken = tokens[i - 1]
          if (currToken && currToken.type === 'scene_heading') {
            scene = breaks.pop()
          }
          if (broken[0]) {
            if (scene) breaks.push(scene)
            breaks.push(broken[0])
          }
          breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
          if (!broken[0] && scene) { // push scene heading along down to next page
            breaks.push(scene)
            leftover += scene.lines
          }
          if (broken[1]) {
            breaks.push(broken[1])
            leftover += broken[1].lines
          }
        } else { // just insert page break
          const currToken = tokens[i - 1]
          if (currToken && currToken.type === 'scene_heading') { // check for previous scene heading, don't want it at end of page
            const scene = breaks.pop()
            breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
            breaks.push(scene)
            breaks.push(token)
            leftover = token.lines + scene.lines
          } else {
            breaks.push({ type: 'page_end' }, { type: 'page_break', logical_page: pages })
            breaks.push(token)
            leftover = token.lines
          }
        }
      }
      if (!dialogueBlock) { // if we're in a dialogue block, we're still calculating where page break goes, if not start over
        lineCount = leftover // reset line count
        pages++ // new page
      }
    } else { // we're not breaking a page but need to track some stuff
      if (token.type === 'page_break') { // WAIT! we have a manual page break, so we are breaking the page!
        log.trace('+++++++ PAGE BREAK (MANUAL) +++++++++')
        lineCount = 0
        token.logical_page = pages
        pages++
      }
      if (token.type === 'character') speaking = token // in case we get a break in dialogue
      if (token.type === 'dialogue' && endedOnParen) { // we have to add a CONT'D after ending a page on parenthesis
        const post = `${speaking.text} (CONT'D)`
        const wrapped = wrap(post, { width: widths.character, indent: '' })
        breaks.push({ type: 'character', text: post, input: post, wrapped, break: true, lines: wrapped.split('\n').length, blanks: 0 })
        endedOnParen = false // reset
      }
      if (token.type === 'page_break') breaks.push({ type: 'page_end' })
      breaks.push(token)
    }
  }
  return breaks // we did it!
}

function breakLines (token, diff, remaining) {
  if (remaining < 4) {
    return [undefined, token] // break before, whole token is pt2
  }
  const sentenced = splitter.sentences(token.wrapped)
  const sentenceCount = sentenced.length
  let new1
  let new2
  const before = token.wrapped.split('\n')

  if (sentenceCount === 1) {
    const after = before.splice(diff)
    const pt1 = before.join('\n').replace(/\s\n/g, '')
    const pt2 = after.join('\n').replace(/\s\n/g, '')
    const wrapped1 = before.join('\n')
    const wrapped2 = wrap(pt2, { width: widths[token.type], indent: '' })
    if (pt1 !== '') {
      new1 = { type: token.type, text: pt1, input: pt1, lines: before.length + 1, wrapped: wrapped1 }
    }
    if (pt2 !== '') {
      new2 = { type: token.type, text: pt2, input: pt2, lines: after.length, wrapped: wrapped2 }
    }
    return [new1, new2]
  } else {
    for (let i = sentenceCount - 1; i > 0; i--) {
      const before = sentenced.slice(0, i)
      const after = sentenced.slice(i)
      const addingLines = before.join(' ').split('\n')
      if (addingLines.length < remaining) {
        const pt1 = before.join(' ').replace(/\s\n/g, '')
        const pt2 = after.join(' ').replace(/\s\n/g, '')
        const wrapped1 = wrap(pt1, { width: widths[token.type], indent: '' })
        const wrapped2 = wrap(pt2, { width: widths[token.type], indent: '' })
        const len1 = wrapped1.split('\n').length
        const len2 = wrapped2.split('\n').length
        if (pt1 !== '') {
          new1 = { type: token.type, text: pt1, input: pt1, lines: len1, wrapped: wrapped1 }
        }
        if (pt2 !== '') {
          new2 = { type: token.type, text: pt2, input: pt2, lines: len2, wrapped: wrapped2 }
        }
        return [new1, new2]
      }
    }
    if (!new1 && !new2) {
      console.log('could not break token!')
      return [undefined, token]
    }
  }
}

const convertToStructure = function (script) {
  return parser(script, insertPageBreaks)
}

module.exports = convertToStructure
